import { Box, Text, Flex, IconButton, useDisclosure, Input, HStack, Textarea } from "@chakra-ui/react";
import { IVacancyApplicationEvent } from "../../api/vacancyApplications/types";
import { useEffect, useRef, useState } from "react";
import { EditIcon, DeleteIcon, CloseIcon } from "@chakra-ui/icons";
import { MainModal } from "../MainModal/MainModal";
import { VacancyApplicationStatusSelect } from "../Controls/VacancyApplicationSelect";
import { useFormik } from "formik";
import { IChangeVacancyApplicationStatus } from "../../api/vacancyApplications/types";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { deleteVacancyApplicationEvent, fetchEventVacancyApplicationUpdate } from "../../store/slices/vacancyApplications";
import { useParams } from "react-router-dom";
import { fetchVacancyApplicationById } from "../../store/slices/vacancyApplications";
interface AnnexEventsBlockProps {
  events: IVacancyApplicationEvent[];
}

export const AnnexEventsBlock = ({ events }: AnnexEventsBlockProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const editingCommentRef = useRef<HTMLDivElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingEventId, setEditingEventId] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { currentVacancyApplication } = useAppSelector((state: RootState) => state.vacancyApplications);

  // Форма для изменения статуса
  const formikStatus = useFormik<IChangeVacancyApplicationStatus>({
    initialValues: {
      status: "",
      scheduled_at: "",
    },
    onSubmit: () => {
      if (id && editingEventId) {
        dispatch(
          fetchEventVacancyApplicationUpdate({
            id: String(editingEventId),
            params: formikStatus.values,
          }),
        ).finally(() => {
          onClose();
          formikStatus.resetForm();
          setEditingEventId(null);
          dispatch(fetchVacancyApplicationById(id));
        });
      }
    },
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (events.length > 0) {
      if (isInitialLoad) {
        setIsInitialLoad(false);
      } else {
        scrollToBottom();
      }
    }
  }, [events, isInitialLoad]);

  useEffect(() => {
    if (editingCommentId && editingCommentRef.current) {
      editingCommentRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editingCommentId]);
  const [withDate, setWithDate] = useState<boolean>(false);
  const [withTime, setWithTime] = useState<boolean>(false);

  // Обработчик редактирования статуса
  const handleEditStatus = (event: IVacancyApplicationEvent) => {
    if (event.type === "status") {
      setEditingEventId(Number(event.id));
      formikStatus.setFieldValue("status", event.value.id);
      setWithDate(!!event.scheduled_at);
      if (event.value.name.includes("Собеседование")) {
        setWithTime(true);
      } else {
        setWithTime(false);
      }
      if (event.scheduled_at) {
        formikStatus.setFieldValue("scheduled_at", event.scheduled_at);
      }
      onOpen();
    }
  };

  // Заглушки для будущей реализации
  const handleStartEditComment = (event: IVacancyApplicationEvent) => {
    if (event.type === "comment") {
      setEditingCommentId(Number(event.id));
      setEditingText(event.value);
    }
  };

  const handleSaveComment = () => {
    dispatch(
      fetchEventVacancyApplicationUpdate({
        id: String(editingCommentId),
        params: {
          comment: editingText,
        },
      }),
    ).finally(() => {
      setEditingCommentId(null);
      setEditingText("");
      id && dispatch(fetchVacancyApplicationById(id));
    });
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingText("");
  };

  const handleDeleteComment = (eventId: number) => {
    if (id) {
      dispatch(deleteVacancyApplicationEvent(String(eventId))).finally(() => {
        dispatch(fetchVacancyApplicationById(id));
      });
    }
  };

  return (
    <>
      <Box
        maxH="45vh"
        overflowY="auto"
        display="flex"
        flexDirection="column-reverse"
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            width: "6px",
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "24px",
          },
        }}
      >
        <div ref={messagesEndRef} />
        {events.map((event, index) =>
          event.type === "status" ? (
            <Flex key={index} justify="center" my={4}>
              <Box bg="gray.100" px={4} py={2} borderRadius="12px" maxW="80%" textAlign="center" position="relative" p={6}>
                <IconButton
                  aria-label="Редактировать статус"
                  icon={<EditIcon />}
                  size="sm"
                  position="absolute"
                  right="1"
                  top="1"
                  variant="ghost"
                  onClick={() => handleEditStatus(event)}
                  sx={{
                    "&:hover": {
                      opacity: 0.6,
                    },
                  }}
                />
                <Text fontSize="sm" color="gray.600">
                  {event.created_by?.username},{" "}
                  {new Date(event.created_at * 1000).toLocaleString("ru-RU", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  })}
                </Text>
                <Text fontWeight={500}>Статус изменен — {event.value.name}</Text>
                {!!event.scheduled_at && (
                  <>
                    {event.value.name.includes("Собеседование") ? (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        Запланировано на:{" "}
                        {new Date(event.scheduled_at * 1000).toLocaleString("ru-RU", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        Запланировано на:{" "}
                        {new Date(event.scheduled_at * 1000).toLocaleString("ru-RU", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </Text>
                    )}
                  </>
                )}
                {event?.reason?.name && <Text fontSize="sm" color="gray.600" mt={1}>{`Причина: ${event.reason.name}`}</Text>}
              </Box>
            </Flex>
          ) : (
            <Flex key={index} justify="start" my={4}>
              <Box bg="gray.100" px={4} py={2} borderRadius="12px" maxW="40%" position="relative" p={6} minW="400px" w="40%">
                <Text fontSize="sm" color="gray.600">
                  {event.created_by?.username},{" "}
                  {new Date(event.created_at * 1000).toLocaleString("ru-RU", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  })}
                </Text>
                {editingCommentId === Number(event.id) ? (
                  <Flex flexDirection="column" gap="8px" mt="8px">
                    <Textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      minH="100px"
                      resize="vertical"
                      w="100%"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveComment();
                        }
                      }}
                    />
                    <HStack spacing={2}>
                      <IconButton aria-label="Сохранить" icon={<EditIcon />} size="sm" onClick={handleSaveComment} colorScheme="green" />
                      <IconButton aria-label="Отменить" icon={<CloseIcon />} size="sm" onClick={handleCancelEdit} colorScheme="red" />
                    </HStack>
                  </Flex>
                ) : (
                  <>
                    <Text fontWeight={500}>Оставлен комментарий</Text>
                    <Text mt={2}>{event.value}</Text>
                    <HStack spacing={2} position="absolute" right="1" top="1">
                      <IconButton
                        aria-label="Редактировать комментарий"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEditComment(event)}
                        sx={{
                          "&:hover": {
                            opacity: 0.6,
                          },
                        }}
                      />
                      <IconButton
                        aria-label="Удалить комментарий"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteComment(Number(event.id))}
                        sx={{
                          "&:hover": {
                            opacity: 0.6,
                          },
                        }}
                      />
                    </HStack>
                  </>
                )}
              </Box>
            </Flex>
          ),
        )}
      </Box>

      <MainModal
        onConfirm={formikStatus.handleSubmit}
        isOpen={isOpen}
        onClose={onClose}
        title="Редактирование этапа"
        isDisabled={!formikStatus.values.status || (withDate && !formikStatus.values.scheduled_at)}
      >
        <Flex flexDirection="column" gap="16px">
          <Flex flexDirection="column" gap="8px">
            <VacancyApplicationStatusSelect
              vacancyId={currentVacancyApplication?.vacancy?.id || 0}
              selectedValue={formikStatus.values.status}
              name="status"
              onChange={(el) => {
                formikStatus.setFieldValue("scheduled_at", "");
                formikStatus.setFieldValue("status", el?.value);
                setWithDate(el?.is_sheduled);
                if (el.label.includes("Собеседование")) {
                  setWithTime(true);
                } else {
                  setWithTime(false);
                }
              }}
            />
            {withDate && (
              <Flex flexDirection="column" gap="8px">
                <Text>Дата</Text>
                <Flex gap="16px">
                  <Input
                    type="date"
                    name="scheduled_at"
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      const currentTime = formikStatus.values.scheduled_at ? new Date(Number(formikStatus.values.scheduled_at) * 1000) : new Date();
                      date.setHours(currentTime.getHours());
                      date.setMinutes(currentTime.getMinutes());
                      formikStatus.setFieldValue("scheduled_at", Math.floor(date.getTime() / 1000));
                    }}
                    value={
                      formikStatus.values.scheduled_at ? new Date(Number(formikStatus.values.scheduled_at) * 1000).toISOString().split("T")[0] : ""
                    }
                  />
                  {withTime && (
                    <Input
                      type="time"
                      onChange={(e) => {
                        const time = e.target.value.split(":");
                        const currentDate = formikStatus.values.scheduled_at ? new Date(Number(formikStatus.values.scheduled_at) * 1000) : new Date();
                        currentDate.setHours(Number(time[0]));
                        currentDate.setMinutes(Number(time[1]));
                        formikStatus.setFieldValue("scheduled_at", Math.floor(currentDate.getTime() / 1000));
                      }}
                      value={
                        formikStatus.values.scheduled_at ? new Date(Number(formikStatus.values.scheduled_at) * 1000).toTimeString().slice(0, 5) : ""
                      }
                    />
                  )}
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      </MainModal>
    </>
  );
};
